'use client'

/**
 * RedditRadarBoard — trending tickers across the big trading subreddits. Sortable by Engagement
 * (upvotes + comments, the default — what the crowd is actually piling into), Mentions (raw volume),
 * or Hot debate (comments per upvote, the discussion-intensity signal). Each row shows upvotes,
 * comments and mention count, a velocity badge vs the last scan, a bull/bear lean, a live day-move,
 * and the most-engaging sample posts behind a dropdown. Every symbol links to its stock hub.
 */

import { useEffect, useMemo, useState } from 'react'
import Link from 'next/link'
import { Flame, TrendingUp, TrendingDown, MessageSquare, ArrowBigUp } from 'lucide-react'
import type { RedditTicker, RedditRadar } from '@/lib/dashboard/redditRadar'
import TickerLogo from '@/components/TickerLogo'

type SortKey = 'heating' | 'engagement' | 'mentions' | 'debate'
const SORTS: { key: SortKey; label: string; hint: string }[] = [
  { key: 'heating', label: '🔥 Heating up', hint: 'biggest jump in mentions vs the window just before - names igniting right now' },
  { key: 'engagement', label: 'Engagement', hint: 'upvotes + comments, what the crowd is actually piling into' },
  { key: 'mentions', label: 'Most mentioned', hint: 'raw number of posts naming the ticker' },
  { key: 'debate', label: 'Hot debate', hint: 'comments per upvote, the threads people are arguing over' },
]
// Heating rank: emerging names first, then by velocity %, then by mentions as the tiebreaker.
const heatOf = (t: RedditTicker) => (t.emerging ? 100000 : 0) + (t.velocityPct ?? -1) * 10 + t.mentions
const metricOf = (t: RedditTicker, k: SortKey) => (k === 'heating' ? heatOf(t) : k === 'mentions' ? t.mentions : k === 'debate' ? (t.debate ?? 0) : (t.engagement ?? 0))
const fmt = (n: number) => (n >= 1000 ? (n / 1000).toFixed(n >= 10000 ? 0 : 1) + 'k' : String(n))
// Window toggle: "Now" = the live snapshot (richest — includes comment mentions). The dated windows re-rank
// from the DB post-history (post mentions only, no comment timestamps), so they fill in as history accrues.
const WINDOWS: { label: string; h: number | null }[] = [
  { label: 'Now', h: null }, { label: '1d', h: 24 }, { label: '3d', h: 72 }, { label: '7d', h: 168 },
]

export default function RedditRadarBoard({ initial }: { initial: RedditRadar }) {
  const [data, setData] = useState<RedditRadar>(initial)
  const [moves, setMoves] = useState<Record<string, number>>({})
  const [sort, setSort] = useState<SortKey>('engagement')
  const [winH, setWinH] = useState<number | null>(null) // null = live snapshot; else DB-history window in hours
  const [winData, setWinData] = useState<RedditRadar | null>(null)
  const [winLoading, setWinLoading] = useState(false)

  // Fetch the DB-history window when a dated window is picked.
  useEffect(() => {
    if (winH == null) { setWinData(null); return }
    let alive = true
    setWinLoading(true)
    fetch(`/api/dashboard/reddit-radar/window?h=${winH}`, { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (alive) setWinData(j && Array.isArray(j.tickers) ? j : null) })
      .catch(() => { if (alive) setWinData(null) })
      .finally(() => { if (alive) setWinLoading(false) })
    return () => { alive = false }
  }, [winH])
  // The ticker set in view: the live snapshot ("Now") or the windowed DB aggregation.
  const viewData = winH == null ? data : (winData ?? { at: 0, tickers: [], posts: [], postsScanned: 0 })

  // If the SSR snapshot was empty (cron not run yet), poll the API which kicks a refresh.
  useEffect(() => {
    if (data.tickers.length) return
    let alive = true
    const id = setInterval(() => {
      fetch('/api/dashboard/reddit-radar', { cache: 'no-store' }).then((r) => (r.ok ? r.json() : null)).then((j) => {
        if (alive && j?.tickers?.length) { setData(j); clearInterval(id) }
      }).catch(() => {})
    }, 8000)
    return () => { alive = false; clearInterval(id) }
  }, [data.tickers.length])

  // Live day-move for the trending symbols (whichever set is in view).
  useEffect(() => {
    const syms = viewData.tickers.map((t) => t.symbol).slice(0, 60)
    if (!syms.length) return
    let alive = true
    const pull = () =>
      fetch(`/api/watchlist/quotes?symbols=${encodeURIComponent(syms.join(','))}`, { cache: 'no-store' })
        .then((r) => (r.ok ? r.json() : null))
        .then((j) => {
          if (!alive || !Array.isArray(j?.quotes)) return
          const m: Record<string, number> = {}
          for (const q of j.quotes) if (q?.symbol && q.chgPct != null) m[String(q.symbol).toUpperCase()] = Number(q.chgPct)
          setMoves(m)
        })
        .catch(() => {})
    pull()
    const id = setInterval(() => { if (!document.hidden) pull() }, 30000)
    return () => { alive = false; clearInterval(id) }
  }, [viewData.tickers])

  const ranked = useMemo(() => {
    const arr = [...viewData.tickers]
    arr.sort((a, b) => (metricOf(b, sort) - metricOf(a, sort)) || (b.engagement ?? 0) - (a.engagement ?? 0))
    return arr
  }, [viewData.tickers, sort])
  const maxMetric = useMemo(() => Math.max(1, ...ranked.map((t) => metricOf(t, sort))), [ranked, sort])
  const when = data.at ? new Date(data.at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : null

  if (!data.tickers.length && winH == null) {
    return <div className="rounded-2xl border border-zinc-800/70 bg-zinc-900/30 px-4 py-12 text-center text-sm text-zinc-500">Scanning the subreddits. Trending tickers will appear here shortly.</div>
  }

  return (
    <div>
      {/* window control */}
      <div className="mb-2 flex flex-wrap items-center gap-1.5">
        <span className="mr-1 text-[11px] font-semibold uppercase tracking-wide text-zinc-600">Window</span>
        {WINDOWS.map((w) => (
          <button key={w.label} onClick={() => setWinH(w.h)} title={w.h == null ? 'Live now (includes comment mentions)' : `Ranked by posts in the last ${w.label}`}
            className={`rounded-full border px-3 py-1 text-[12px] font-semibold transition-colors ${winH === w.h ? 'border-orange-500/60 bg-orange-500/15 text-orange-200' : 'border-zinc-800 bg-zinc-900/40 text-zinc-400 hover:border-zinc-700 hover:text-zinc-200'}`}>
            {w.label}
          </button>
        ))}
      </div>
      {/* sort control */}
      <div className="mb-3 flex flex-wrap items-center gap-1.5">
        <span className="mr-1 text-[11px] font-semibold uppercase tracking-wide text-zinc-600">Sort</span>
        {SORTS.map((s) => (
          <button
            key={s.key}
            onClick={() => setSort(s.key)}
            title={s.hint}
            className={`rounded-full border px-3 py-1 text-[12px] font-semibold transition-colors ${
              sort === s.key
                ? 'border-orange-500/60 bg-orange-500/15 text-orange-200'
                : 'border-zinc-800 bg-zinc-900/40 text-zinc-400 hover:border-zinc-700 hover:text-zinc-200'
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>
      {winH == null
        ? (when && <p className="mb-3 text-[11px] text-zinc-600">Updated {when}. Scanned {data.postsScanned.toLocaleString()} posts across r/wallstreetbets, r/stocks, r/investing and more (hot, new and top of the day).</p>)
        : <p className="mb-3 text-[11px] text-zinc-600">{winLoading ? 'Loading window…' : `Ranked by ${viewData.postsScanned.toLocaleString()} posts in the last ${WINDOWS.find((w) => w.h === winH)?.label}. Comment mentions are counted only in the live "Now" view.`}</p>}
      {ranked.length ? (
      <ol className="overflow-hidden rounded-2xl border border-zinc-800/70 bg-zinc-900/30">
        {ranked.map((t, i) => <Row key={t.symbol} t={t} rank={i + 1} maxMetric={maxMetric} sort={sort} move={moves[t.symbol]} first={i === 0} />)}
      </ol>
      ) : (
        <div className="rounded-2xl border border-zinc-800/70 bg-zinc-900/30 px-4 py-10 text-center text-sm text-zinc-500">
          {winLoading ? 'Loading…' : 'Not enough post history for this window yet. It fills in as we keep scanning; try "Now" or a shorter window.'}
        </div>
      )}
    </div>
  )
}

function Row({ t, rank, maxMetric, sort, move, first }: { t: RedditTicker; rank: number; maxMetric: number; sort: SortKey; move: number | undefined; first: boolean }) {
  const total = t.bull + t.bear
  const bullPct = total > 0 ? (t.bull / total) * 100 : 50
  const lean = t.bull > t.bear ? 'bull' : t.bear > t.bull ? 'bear' : 'even'
  const upvotes = t.upvotes ?? t.score ?? 0
  const comments = t.comments ?? 0
  const barPct = (metricOf(t, sort) / maxMetric) * 100
  return (
    <li className={`group/r ${first ? '' : 'border-t border-zinc-800/60'}`}>
      <details>
        <summary className="grid cursor-pointer list-none grid-cols-[auto_auto_1fr_auto] items-center gap-2.5 px-2.5 py-2.5 transition-colors hover:bg-zinc-800/40 sm:gap-3 sm:px-3">
          <span className="w-5 text-right text-[12px] font-bold tabular-nums text-zinc-600">{rank}</span>
          <TickerLogo symbol={t.symbol} size={32} rounded="rounded-lg" />
          <span className="min-w-0">
            <span className="flex items-center gap-2">
              <Link href={`/stocks/${encodeURIComponent(t.symbol)}`} onClick={(e) => e.stopPropagation()} className="text-[15px] font-extrabold tracking-tight text-white hover:text-amber-300">${t.symbol}</Link>
              {move != null && <span className={`text-[11px] font-bold tabular-nums ${move >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>{move >= 0 ? '+' : ''}{move.toFixed(1)}%</span>}
            </span>
            {/* metric bar (reflects the active sort) + engagement stats */}
            <span className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className="h-1 w-14 shrink-0 overflow-hidden rounded-full bg-zinc-800 sm:w-20"><span className="block h-full rounded-full bg-amber-500/80" style={{ width: `${barPct}%` }} /></span>
              <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] tabular-nums text-zinc-500">
                <span className="inline-flex items-center gap-0.5" title={`${upvotes.toLocaleString()} upvotes`}><ArrowBigUp className="h-3 w-3 text-orange-400/80" />{fmt(upvotes)}</span>
                <span className="inline-flex items-center gap-0.5" title={`${comments.toLocaleString()} comments`}><MessageSquare className="h-2.5 w-2.5 text-sky-400/80" />{fmt(comments)}</span>
                <span className="text-zinc-600">{t.mentions} post{t.mentions === 1 ? '' : 's'} · {t.subreddits.length} sub{t.subreddits.length === 1 ? '' : 's'}</span>
              </span>
            </span>
          </span>
          <span className="flex shrink-0 flex-col items-end gap-1">
            <span className="flex items-center gap-1.5">
              <span className="inline-flex items-center gap-1 text-[13px] font-bold tabular-nums text-amber-300">{sort === 'debate' ? `${(t.debate ?? 0).toFixed(1)}×` : sort === 'mentions' || sort === 'heating' ? t.mentions : fmt(t.engagement ?? 0)}</span>
              {t.velocityPct != null && t.velocityPct >= 15 && <span className="inline-flex items-center gap-0.5 rounded bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-bold text-emerald-300"><TrendingUp className="h-2.5 w-2.5" />{Math.round(t.velocityPct)}%</span>}
              {t.velocityPct != null && t.velocityPct <= -15 && <span className="inline-flex items-center gap-0.5 rounded bg-zinc-700/50 px-1.5 py-0.5 text-[10px] font-semibold text-zinc-400"><TrendingDown className="h-2.5 w-2.5" />{Math.round(t.velocityPct)}%</span>}
              {t.emerging && <span className="inline-flex items-center gap-0.5 rounded bg-amber-500/20 px-1.5 py-0.5 text-[10px] font-bold text-amber-300" title="~0 mentions in the prior window, buzzing now"><Flame className="h-2.5 w-2.5" />new</span>}
            </span>
            {total > 0 && (
              <span className="flex items-center gap-1.5" title={`${t.bull} bullish · ${t.bear} bearish posts`}>
                <span className="h-1.5 w-12 overflow-hidden rounded-full bg-rose-500/40 sm:w-16"><span className="block h-full rounded-full bg-emerald-500" style={{ width: `${bullPct}%` }} /></span>
                <span className={`text-[10px] font-semibold ${lean === 'bull' ? 'text-emerald-400' : lean === 'bear' ? 'text-rose-400' : 'text-zinc-500'}`}>{lean === 'bull' ? 'Bullish' : lean === 'bear' ? 'Bearish' : 'Mixed'}</span>
              </span>
            )}
          </span>
        </summary>
        {t.samples.length > 0 && (
          <div className="space-y-1 border-t border-zinc-800/50 bg-zinc-950/40 px-3 py-2 pl-11">
            {t.samples.map((s, j) => (
              <a key={j} href={s.url} target="_blank" rel="noopener noreferrer" className="flex items-start gap-2 rounded-lg px-1.5 py-1 text-[12px] text-zinc-400 transition-colors hover:bg-white/5 hover:text-zinc-200">
                <span className="mt-0.5 shrink-0 text-[10px] font-semibold text-zinc-600">r/{s.sub}</span>
                <span className="min-w-0 flex-1 line-clamp-1">{s.title}</span>
                <span className="mt-0.5 flex shrink-0 items-center gap-1.5 text-[10px] tabular-nums text-zinc-600">
                  <span className="inline-flex items-center gap-0.5" title="upvotes"><ArrowBigUp className="h-3 w-3 text-orange-400/70" />{fmt(s.score)}</span>
                  <span className="inline-flex items-center gap-0.5" title="comments"><MessageSquare className="h-2.5 w-2.5 text-sky-400/70" />{fmt(s.comments ?? 0)}</span>
                  {s.upvoteRatio > 0 && <span className="text-emerald-400/70" title="upvoted">{Math.round(s.upvoteRatio * 100)}%</span>}
                </span>
              </a>
            ))}
          </div>
        )}
      </details>
    </li>
  )
}
